import { useState, useCallback, useMemo } from 'react';
import { IntegrationCanvasNode, IntegrationFlow, ConnectorType, ConnectorConfig, MappingConfig, DestinationConfig, TriggerConfig, FlowType, FLOW_TYPE_LABELS } from '../types';
import { getDefaultConnectorConfig } from '../constants/connector-configs';

let nodeIdCounter = 0;
const generateId = () => `node-${++nodeIdCounter}`;

let flowIdCounter = 0;
const generateFlowId = () => `flow-${++flowIdCounter}`;

const FLOW_Y_SPACING = 250;

export interface UseIntegrationFlowReturn {
  // Multi-flow state
  flows: IntegrationFlow[];
  activeFlowId: string | null;
  setActiveFlowId: (id: string | null) => void;

  // Flow management
  addFlow: (flowType: FlowType, connectorType: ConnectorType, x: number, y: number) => void;
  removeFlow: (flowId: string) => void;
  changeFlowType: (flowId: string, newType: FlowType) => void;
  availableFlowTypes: FlowType[];
  currentConnectorType: ConnectorType | null;

  // All nodes flattened (for canvas rendering)
  nodes: IntegrationCanvasNode[];
  allNodes: IntegrationCanvasNode[];

  // Active flow convenience getters
  activeConnectorType: ConnectorType | null;
  triggerNode: IntegrationCanvasNode | null;
  sourceNode: IntegrationCanvasNode | null;
  mappingNode: IntegrationCanvasNode | null;
  destinationNode: IntegrationCanvasNode | null;

  // Node operations (find flow automatically from node id)
  addSourceNode: (type: ConnectorType, x: number, y: number) => void;
  updateNodePosition: (id: string, deltaX: number, deltaY: number) => void;
  updateNodeConfig: (id: string, config: any) => void;
  deleteNode: (id: string) => void;
  resetFlow: () => void;

  // Viewport
  zoom: number;
  panX: number;
  panY: number;
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  recenter: (viewportWidth: number, viewportHeight: number) => void;

  // Backend serialization
  loadFromBackend: (data: any) => void;
  toBackendPayload: () => any | null;
  isLoaded: boolean;
}

function createFlowNodes(
  flowId: string,
  type: ConnectorType,
  flowType: FlowType,
  x: number,
  y: number,
  sourceConfig?: ConnectorConfig,
): IntegrationCanvasNode[] {
  const connectorName = type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
  const defaultMapping: MappingConfig = { mappings: [], autoMapped: false };
  const defaultDestination: DestinationConfig = { matchField: 'sku', syncBehavior: flowType };
  const defaultTrigger: TriggerConfig = { syncMode: 'manual' };

  return [
    {
      id: generateId(),
      flowId,
      type: 'trigger',
      title: 'PAGES.API_INTEGRATION.NODE_TITLES.TRIGGER',
      subtitle: 'PAGES.API_INTEGRATION.NODE_SUBTITLES.MANUAL_SYNC',
      x,
      y,
      config: defaultTrigger,
    },
    {
      id: generateId(),
      flowId,
      type: 'source',
      connectorType: type,
      title: connectorName,
      subtitle: sourceConfig ? 'PAGES.API_INTEGRATION.NODE_SUBTITLES.CONNECTED' : 'PAGES.API_INTEGRATION.NODE_SUBTITLES.CONFIGURE_CONNECTION',
      x: x + 180,
      y,
      config: sourceConfig || getDefaultConnectorConfig(type),
    },
    {
      id: generateId(),
      flowId,
      type: 'mapping',
      title: 'PAGES.API_INTEGRATION.NODE_TITLES.DATA_MAPPING',
      subtitle: 'PAGES.API_INTEGRATION.NODE_SUBTITLES.FIELDS_MAPPED:0',
      x: x + 430,
      y,
      config: defaultMapping,
    },
    {
      id: generateId(),
      flowId,
      type: 'destination',
      title: 'PAGES.API_INTEGRATION.NODE_TITLES.ARDEALY_SYNC',
      subtitle: 'PAGES.API_INTEGRATION.NODE_SUBTITLES.CONFIGURE_SYNC',
      x: x + 680,
      y,
      config: defaultDestination,
    },
  ];
}

export const useIntegrationFlow = (): UseIntegrationFlowReturn => {
  const [flows, setFlows] = useState<IntegrationFlow[]>([]);
  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
  const [zoom, setZoomState] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // --- Computed ---

  const allNodes = useMemo(
    () => flows.flatMap((f) => f.nodes),
    [flows],
  );

  const activeFlow = useMemo(
    () => flows.find((f) => f.id === activeFlowId) ?? null,
    [flows, activeFlowId],
  );

  const activeConnectorType = useMemo(() => {
    const source = activeFlow?.nodes.find((n) => n.type === 'source');
    return source?.connectorType ?? null;
  }, [activeFlow]);

  const triggerNode = useMemo(
    () => activeFlow?.nodes.find((n) => n.type === 'trigger') ?? null,
    [activeFlow],
  );
  const sourceNode = useMemo(
    () => activeFlow?.nodes.find((n) => n.type === 'source') ?? null,
    [activeFlow],
  );
  const mappingNode = useMemo(
    () => activeFlow?.nodes.find((n) => n.type === 'mapping') ?? null,
    [activeFlow],
  );
  const destinationNode = useMemo(
    () => activeFlow?.nodes.find((n) => n.type === 'destination') ?? null,
    [activeFlow],
  );

  // --- Viewport ---

  const setZoom = useCallback((value: number) => {
    setZoomState(Math.max(0.25, Math.min(2, value)));
  }, []);

  const setPan = useCallback((x: number, y: number) => {
    setPanX(x);
    setPanY(y);
  }, []);

  const recenter = useCallback(
    (viewportWidth: number, viewportHeight: number) => {
      const all = flows.flatMap((f) => f.nodes);
      if (all.length === 0) {
        setZoomState(1);
        setPanX(0);
        setPanY(0);
        return;
      }
      const getNodeWidth = (n: IntegrationCanvasNode) =>
        n.type === 'trigger' ? 104 : 180;
      const minX = Math.min(...all.map((n) => n.x));
      const maxX = Math.max(...all.map((n) => n.x + getNodeWidth(n)));
      const minY = Math.min(...all.map((n) => n.y));
      const maxY = Math.max(...all.map((n) => n.y + 90));
      const contentW = maxX - minX + 120;
      const contentH = maxY - minY + 120;
      const newZoom = Math.min(1, viewportWidth / contentW, viewportHeight / contentH);
      const clampedZoom = Math.max(0.25, Math.min(2, newZoom));
      setPanX(
        (viewportWidth - contentW * clampedZoom) / 2 -
          minX * clampedZoom +
          60 * clampedZoom,
      );
      setPanY(
        (viewportHeight - contentH * clampedZoom) / 2 -
          minY * clampedZoom +
          60 * clampedZoom,
      );
      setZoomState(clampedZoom);
    },
    [flows],
  );

  // --- Flow management ---

  // Which flow types are still available to add
  const availableFlowTypes = useMemo((): FlowType[] => {
    if (flows.length >= 2) return [];
    const usedTypes = new Set(flows.map((f) => f.flowType));
    return (['create_and_update', 'update_only'] as FlowType[]).filter(
      (ft) => !usedTypes.has(ft),
    );
  }, [flows]);

  // The connector type used by existing flows (all flows share the same connector)
  const currentConnectorType = useMemo((): ConnectorType | null => {
    const source = flows
      .flatMap((f) => f.nodes)
      .find((n) => n.type === 'source');
    return source?.connectorType ?? null;
  }, [flows]);

  const addFlow = useCallback(
    (flowType: FlowType, connectorType: ConnectorType, x: number, y: number) => {
      setFlows((prev) => {
        // Enforce max 2 flows
        if (prev.length >= 2) return prev;
        // Enforce no duplicate flow types
        if (prev.some((f) => f.flowType === flowType)) return prev;

        const flowId = generateFlowId();

        // Reuse credentials from existing flow
        const existingSource = prev
          .flatMap((f) => f.nodes)
          .find((n) => n.type === 'source');
        const existingConfig = existingSource
          ? { ...(existingSource.config as ConnectorConfig) }
          : undefined;

        const nodes = createFlowNodes(flowId, connectorType, flowType, x, y, existingConfig);
        const newFlow: IntegrationFlow = {
          id: flowId,
          name: FLOW_TYPE_LABELS[flowType],
          flowType,
          nodes,
        };
        return [...prev, newFlow];
      });
    },
    [],
  );

  const changeFlowType = useCallback(
    (flowId: string, newType: FlowType) => {
      setFlows((prev) => {
        // Don't allow duplicate flow types
        if (prev.some((f) => f.id !== flowId && f.flowType === newType)) return prev;
        return prev.map((f) => {
          if (f.id !== flowId) return f;
          // Update the flow type, name, and destination node's syncBehavior
          const updatedNodes = f.nodes.map((n) => {
            if (n.type !== 'destination') return n;
            const destConfig = (n.config || {}) as DestinationConfig;
            return { ...n, config: { ...destConfig, syncBehavior: newType } };
          });
          return { ...f, flowType: newType, name: FLOW_TYPE_LABELS[newType], nodes: updatedNodes };
        });
      });
    },
    [],
  );

  const removeFlow = useCallback(
    (flowId: string) => {
      setFlows((prev) => prev.filter((f) => f.id !== flowId));
      setActiveFlowId((prev) => (prev === flowId ? null : prev));
    },
    [],
  );

  // --- Backward-compat: addSourceNode creates/replaces a single flow ---

  const addSourceNode = useCallback(
    (type: ConnectorType, x: number, y: number) => {
      const flowId = generateFlowId();
      const flowType: FlowType = 'update_only';
      const nodes = createFlowNodes(flowId, type, flowType, x, y);
      const newFlow: IntegrationFlow = {
        id: flowId,
        name: FLOW_TYPE_LABELS[flowType],
        flowType,
        nodes,
      };
      setFlows([newFlow]);
      setActiveFlowId(flowId);
    },
    [],
  );

  // --- Node operations ---

  const updateNodePosition = useCallback(
    (id: string, deltaX: number, deltaY: number) => {
      setFlows((prev) =>
        prev.map((f) => ({
          ...f,
          nodes: f.nodes.map((node) =>
            node.id === id
              ? { ...node, x: node.x + deltaX, y: node.y + deltaY }
              : node,
          ),
        })),
      );
    },
    [],
  );

  const updateNodeConfig = useCallback((id: string, config: any) => {
    setFlows((prev) =>
      prev.map((f) => ({
        ...f,
        nodes: f.nodes.map((node) => {
          if (node.id !== id) return node;
          const updated = { ...node, config };

          if (node.type === 'mapping') {
            const mappingCfg = config as MappingConfig;
            const count = mappingCfg?.mappings?.length ?? 0;
            updated.subtitle = `PAGES.API_INTEGRATION.NODE_SUBTITLES.FIELDS_MAPPED:${count}`;
          } else if (node.type === 'trigger') {
            const triggerCfg = config as TriggerConfig;
            const mode = triggerCfg?.syncMode ?? 'manual';
            updated.subtitle = `PAGES.API_INTEGRATION.NODE_SUBTITLES.${mode.toUpperCase()}_SYNC`;
          }

          return updated;
        }),
      })),
    );
  }, []);

  const deleteNode = useCallback(
    (id: string) => {
      // Find which flow contains this node, remove entire flow
      setFlows((prev) => {
        const flowContainingNode = prev.find((f) =>
          f.nodes.some((n) => n.id === id),
        );
        if (!flowContainingNode) return prev;
        const remaining = prev.filter((f) => f.id !== flowContainingNode.id);
        return remaining;
      });
      setActiveFlowId((prev) => {
        // If deleted flow was active, clear or switch
        return prev; // will be resolved by the effect below
      });
    },
    [],
  );

  const resetFlow = useCallback(() => {
    setFlows([]);
    setActiveFlowId(null);
  }, []);

  // --- Backend serialization ---

  const loadFromBackend = useCallback((data: any) => {
    if (!data) {
      setIsLoaded(true);
      return;
    }

    // Multi-flow format: data.flows is an array
    if (Array.isArray(data.flows) && data.flows.length > 0) {
      const loadedFlows: IntegrationFlow[] = data.flows.map(
        (flowData: any, flowIndex: number) => {
          const fId = flowData.id || generateFlowId();
          return buildFlowFromBackendData(fId, flowData, flowIndex);
        },
      );
      // Sync counters past loaded IDs to avoid collisions when adding new flows
      for (const lf of loadedFlows) {
        const fm = lf.id.match(/^flow-(\d+)/);
        if (fm && parseInt(fm[1], 10) >= flowIdCounter) {
          flowIdCounter = parseInt(fm[1], 10);
        }
        for (const n of lf.nodes) {
          const nm = n.id.match(/^node-(\d+)/);
          if (nm && parseInt(nm[1], 10) >= nodeIdCounter) {
            nodeIdCounter = parseInt(nm[1], 10);
          }
        }
      }
      setFlows(loadedFlows);
      setActiveFlowId(loadedFlows[0]?.id ?? null);
    } else {
      // Legacy flat format: wrap in a single flow
      const fId = generateFlowId();
      const flow = buildFlowFromBackendData(fId, data, 0);
      setFlows([flow]);
      setActiveFlowId(fId);
    }

    setIsLoaded(true);
  }, []);

  const toBackendPayload = useCallback(() => {
    if (flows.length === 0) return null;

    const flowPayloads = flows
      .map((f) => serializeFlowToBackend(f))
      .filter(Boolean);

    if (flowPayloads.length === 0) return null;

    return { flows: flowPayloads };
  }, [flows]);

  return {
    flows,
    activeFlowId,
    setActiveFlowId,
    addFlow,
    removeFlow,
    changeFlowType,
    availableFlowTypes,
    currentConnectorType,
    nodes: allNodes,
    allNodes,
    activeConnectorType,
    triggerNode,
    sourceNode,
    mappingNode,
    destinationNode,
    addSourceNode,
    updateNodePosition,
    updateNodeConfig,
    deleteNode,
    resetFlow,
    zoom,
    panX,
    panY,
    setZoom,
    setPan,
    recenter,
    loadFromBackend,
    toBackendPayload,
    isLoaded,
  };
};

// --- Helpers ---

function buildFlowFromBackendData(
  flowId: string,
  data: any,
  flowIndex: number,
): IntegrationFlow {
  const connectorType = data.connectorType as ConnectorType;
  const connectorName =
    connectorType.charAt(0).toUpperCase() +
    connectorType.slice(1).replace('_', ' ');

  const baseX = 200;
  const baseY = 200;

  const connectorConfig: ConnectorConfig = {
    connectorType,
    ...data.connectorConfig,
  } as ConnectorConfig;

  const mappingConfig: MappingConfig = {
    mappings: (data.mappings || []).map((m: any, i: number) => ({
      id: `mapping-${i}`,
      sourceField: m.sourceField,
      targetField: m.targetField,
    })),
    autoMapped: data.autoMapped ?? false,
  };

  const triggerConfig: TriggerConfig = {
    syncMode: data.syncMode || 'manual',
    ...(data.frequency ? { frequency: data.frequency } : {}),
  };

  const destinationConfig: DestinationConfig = {
    matchField: data.matchField || 'sku',
    ...(data.syncFields ? { syncFields: data.syncFields } : {}),
    syncBehavior: data.syncBehavior || 'update_only',
  };

  const mappingCount = mappingConfig.mappings.length;
  const syncModeLabel =
    triggerConfig.syncMode.charAt(0).toUpperCase() +
    triggerConfig.syncMode.slice(1);

  const nodes: IntegrationCanvasNode[] = [
    {
      id: generateId(),
      flowId,
      type: 'trigger',
      title: 'PAGES.API_INTEGRATION.NODE_TITLES.TRIGGER',
      subtitle: `PAGES.API_INTEGRATION.NODE_SUBTITLES.${triggerConfig.syncMode.toUpperCase()}_SYNC`,
      x: baseX,
      y: baseY,
      config: triggerConfig,
    },
    {
      id: generateId(),
      flowId,
      type: 'source',
      connectorType,
      title: connectorName,
      subtitle: 'PAGES.API_INTEGRATION.NODE_SUBTITLES.CONNECTED',
      x: baseX + 180,
      y: baseY,
      config: connectorConfig,
    },
    {
      id: generateId(),
      flowId,
      type: 'mapping',
      title: 'PAGES.API_INTEGRATION.NODE_TITLES.DATA_MAPPING',
      subtitle: `PAGES.API_INTEGRATION.NODE_SUBTITLES.FIELDS_MAPPED:${mappingCount}`,
      x: baseX + 430,
      y: baseY,
      config: mappingConfig,
    },
    {
      id: generateId(),
      flowId,
      type: 'destination',
      title: 'PAGES.API_INTEGRATION.NODE_TITLES.ARDEALY_SYNC',
      subtitle: 'PAGES.API_INTEGRATION.NODE_SUBTITLES.CONFIGURE_SYNC',
      x: baseX + 680,
      y: baseY,
      config: destinationConfig,
    },
  ];

  const flowType: FlowType = (data.syncBehavior === 'create_and_update' ? 'create_and_update' : 'update_only');

  return {
    id: flowId,
    name: data.name || FLOW_TYPE_LABELS[flowType],
    flowType,
    nodes,
  };
}

function serializeFlowToBackend(flow: IntegrationFlow): any | null {
  const trigger = flow.nodes.find((n) => n.type === 'trigger');
  const source = flow.nodes.find((n) => n.type === 'source');
  const mapping = flow.nodes.find((n) => n.type === 'mapping');
  const destination = flow.nodes.find((n) => n.type === 'destination');

  if (!source || !mapping || !destination || !trigger) return null;

  const sourceConfig = source.config as ConnectorConfig;
  const mappingCfg = mapping.config as MappingConfig;
  const destConfig = destination.config as DestinationConfig;
  const triggerCfg = trigger.config as TriggerConfig;

  const { connectorType, ...connectorConfig } = sourceConfig;

  return {
    id: flow.id,
    name: flow.name,
    connectorType,
    connectorConfig,
    mappings: mappingCfg.mappings.map(({ sourceField, targetField }) => ({
      sourceField,
      targetField,
    })),
    autoMapped: mappingCfg.autoMapped,
    syncMode: triggerCfg.syncMode,
    matchField: destConfig.matchField,
    syncBehavior: destConfig.syncBehavior || 'update_only',
    ...(destConfig.syncFields ? { syncFields: destConfig.syncFields } : {}),
    ...(triggerCfg.syncMode === 'scheduled' && triggerCfg.frequency
      ? { frequency: triggerCfg.frequency }
      : {}),
  };
}
